import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UATConstants } from '../../Models/UATConstants';
import {
  GroupMember,
  SearchMemberParams,
  IAccountInfo,
  OpportunityData,
  MilestoneData,
  RaidRequestDetails,
  GetRaidRequestParams,
  PaginatedOpportunitiesParams,
  PaginatedOpportunitiesResponse,
  PaginatedMilestonesParams,
  PaginatedMilestonesResponse,
} from '../interface/DashboardResponse';
import { API_CONSTANTS } from '../utils/store-constants';

// UAM API
export const uamApi = createApi({
  reducerPath: 'uamApi',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.MaasMembersWebUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(UATConstants.jwtTokenForApi);
      const coreAPIToken = sessionStorage.getItem(UATConstants.jwtTokenForCoreApi);
      const graphToken = sessionStorage.getItem(UATConstants.jwtTokenForGraph);

      if (token && graphToken && coreAPIToken) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('Coreapitoken', coreAPIToken);
        headers.set('Graphtoken', graphToken);
      }

      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');

      return headers;
    },
  }),
  tagTypes: ['User'],
  endpoints: (builder) => ({
    SearchUsers: builder.mutation<GroupMember[], SearchMemberParams>({
      query: (alias) => ({
        method: 'POST',
        url: '/api/usersTable',
        body: {
          alias,
        },
      }),
    }),
    // Milestones endpoints
    GetByOpportunityWithMilestones: builder.query<MilestoneData, string>({
      query: (opportunityId) => ({
        method: 'GET',
        url: `/api/V2/MSX/GetByOpportunityWithMilestones?opportunityId=${opportunityId}`,
      }),
      providesTags: ['User'],
    }),
    // Paginated milestones endpoint following dashboard pattern
    GetByOpportunityWithMilestonesPaginated: builder.query<
      PaginatedMilestonesResponse,
      PaginatedMilestonesParams
    >({
      query: ({ opportunityId, skipIndex = 1, takeCount = 50 }) => {
        const params = new URLSearchParams({
          skipIndex: skipIndex.toString(),
          takeCount: Math.min(takeCount, 999).toString(), // Cap at 999 like dashboard
        });

        return {
          method: 'GET',
          url: `/api/V2/MSX/GetByOpportunityWithMilestones?opportunityId=${opportunityId}&${params}`,
        };
      },
      providesTags: ['User'],
    }),
  }),
});

export const {
  useSearchUsersMutation,
  useGetByOpportunityWithMilestonesQuery,
  useLazyGetByOpportunityWithMilestonesQuery,
  useGetByOpportunityWithMilestonesPaginatedQuery,
  useLazyGetByOpportunityWithMilestonesPaginatedQuery,
} = uamApi;

// Import the base API for injecting endpoints
import { commonServicesBaseApi } from './commonServicesBaseApi';

// Inject endpoints into the commonServicesBaseApi
const injectedApi = commonServicesBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    GetOpportunitiesByTpId: builder.query<OpportunityData[], string>({
      query: (tpid) => ({
        method: 'GET',
        url: `/api/v1/MSXData/GetOpportunitiesByTpId/${tpid}`,
      }),
      providesTags: ['Opportunity'],
    }),
    // Paginated opportunities endpoint following dashboard pattern
    GetOpportunitiesByTpIdPaginated: builder.query<
      PaginatedOpportunitiesResponse,
      PaginatedOpportunitiesParams
    >({
      query: ({ tpid, skipIndex = 1, takeCount = 50 }) => {
        const params = new URLSearchParams({
          skipIndex: skipIndex.toString(),
          takeCount: Math.min(takeCount, 999).toString(), // Cap at 999 like dashboard
        });

        return {
          method: 'GET',
          url: `/api/v1/MSXData/GetOpportunitiesByTpId/${tpid}?${params}`,
        };
      },
      providesTags: ['Opportunity'],
    }),
    GetRaidRequestDetails: builder.query<RaidRequestDetails, GetRaidRequestParams>({
      query: ({ queryId, raidId, tenantId }) => ({
        method: 'GET',
        url: `/api/v1/raid/transactional/raiditems?queryid=${queryId}&raidid=${raidId}&tenantid=${tenantId}`,
      }),
      providesTags: ['Opportunity'],
    }),
    GetAccountInfoByTpid: builder.query<IAccountInfo[], string>({
      query: (tpid) => ({
        method: 'GET',
        url: `${API_CONSTANTS.GetAccountInfoFromTpID}/${tpid}`,
      }),
    }),
  }),
  overrideExisting: false,
});

// Export the injected API for use in slices that need to use matchers
export const dashboardInjectedApi = injectedApi;

export const {
  useGetOpportunitiesByTpIdQuery,
  useLazyGetOpportunitiesByTpIdQuery,
  useGetOpportunitiesByTpIdPaginatedQuery,
  useLazyGetOpportunitiesByTpIdPaginatedQuery,
  useGetRaidRequestDetailsQuery,
  useLazyGetRaidRequestDetailsQuery,
  useGetAccountInfoByTpidQuery,
  useLazyGetAccountInfoByTpidQuery,
} = injectedApi;
